import { baseApi } from "./baseApi";

const SHIPMENT_ACTION_BASE_URL = "/shipmentAction";

export const shipmentActionApi = baseApi.injectEndpoints({
  tagTypes: ["ShipmentAction"],

  endpoints: (builder) => ({
    getFilteredShipmentActions: builder.query({
      query: (params) => ({
        url: `${SHIPMENT_ACTION_BASE_URL}/filtered`,
        params: { ...params, _t: Date.now() },
      }),
      transformResponse: (response) => ({
        data: response.data.map((item) => ({
          ...item,
          shipment: item.shipment || {
            _id: item.shipmentId,
            shipmentId: item.shipmentId,
          },
          responsible: item.responsible || null,
        })),
        totalItems: response.totalItems,
      }),
      providesTags: (result) =>
        result && Array.isArray(result.data)
          ? [
              ...result.data.map(({ _id }) => ({
                type: "ShipmentAction",
                id: _id,
              })),
              { type: "ShipmentAction", id: "LIST" },
            ]
          : [{ type: "ShipmentAction", id: "LIST" }],
    }),
    getShipmentActionList: builder.query({
      query: (params) => ({
        url: `${SHIPMENT_ACTION_BASE_URL}/list`,
        params: { ...params, _t: Date.now() },
      }),
      transformResponse: (response) => ({
        data: response.data.map((item) => ({
          ...item,
          shipmentId: item.shipmentId || {
            _id: item.shipmentId,
            shipmentId: item.shipmentId,
          },
          responsible: item.responsible || null,
        })),
        totalItems: response.totalItems,
      }),
      providesTags: (result) =>
        result && Array.isArray(result.data)
          ? [
              ...result.data.map(({ _id }) => ({
                type: "ShipmentAction",
                id: _id,
              })),
              { type: "ShipmentAction", id: "LIST" },
            ]
          : [{ type: "ShipmentAction", id: "LIST" }],
    }),

    getShipmentActionListForUser: builder.query({
      query: (params) => ({
        url: `${SHIPMENT_ACTION_BASE_URL}/list/for-user`,
        params: { ...params, _t: Date.now() },
      }),
      providesTags: (result) =>
        result && Array.isArray(result.data)
          ? [
              ...result.data.map(({ _id }) => ({
                type: "ShipmentAction",
                id: _id,
              })),
              { type: "ShipmentAction", id: "LIST" },
            ]
          : [{ type: "ShipmentAction", id: "LIST" }],
    }),

    getShipmentActionNotificationList: builder.query({
      query: ({ vendorId, ...params }) => ({
        url: `${SHIPMENT_ACTION_BASE_URL}/vendor/${vendorId}`,
        params,
      }),
      providesTags: (result) =>
        result && Array.isArray(result.data)
          ? [
              ...result.data.map(({ _id }) => ({
                type: "ShipmentAction",
                id: _id,
              })),
              { type: "ShipmentAction", id: "LIST" },
            ]
          : [{ type: "ShipmentAction", id: "LIST" }],
    }),

    getShipmentActionDetail: builder.query({
      query: (id) => `${SHIPMENT_ACTION_BASE_URL}/${id}`,
      transformResponse: (response) => ({
        ...response,
        shipmentId: response.shipmentId || {
          _id: response.shipmentId,
          shipmentId: response.shipmentId,
        },
        responsible: response.responsible || null,
      }),
      providesTags: (result, error, id) => [
        { type: "ShipmentAction", id },
        { type: "ShipmentAction", id: "LIST" },
      ],
    }),

    getShipmentActionByShipmentId: builder.query({
      query: (shipmentId) => `${SHIPMENT_ACTION_BASE_URL}/list/${shipmentId}`,
      transformResponse: (response) => ({
        data: response.data.map((item) => ({
          ...item,
          shipmentId: item.shipmentId || {
            _id: item.shipmentId,
            shipmentId: item.shipmentId,
          },
          responsible: item.responsible || null,
        })),
        totalItems: response.totalItems,
      }),
      providesTags: (result, error, shipmentId) => [
        { type: "ShipmentAction", shipmentId },
        { type: "ShipmentAction", id: "LIST" },
      ],
    }),

    createShipmentAction: builder.mutation({
      query: (payload) => {
        return {
          url: `${SHIPMENT_ACTION_BASE_URL}/create`,
          method: "POST",
          body: payload,
          headers: {
            "Content-Type": "application/json",
          },
        };
      },
      invalidatesTags: [
        { type: "ShipmentAction", id: "LIST" },
        { type: "ShipmentAction", id: "NOTIFICATION_LIST" },
      ],
    }),

    createShipmentActionBulk: builder.mutation({
      query: (payload) => ({
        url: `${SHIPMENT_ACTION_BASE_URL}/create/bulk`,
        method: "POST",
        body: { actions: payload }, // Wrap actions in an object
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: [
        { type: "ShipmentAction", id: "LIST" },
        { type: "ShipmentAction", id: "NOTIFICATION_LIST" },
      ],
    }),

    updateShipmentAction: builder.mutation({
      query: (payload) => {
        const { id, ...body } = payload;
        return {
          url: `${SHIPMENT_ACTION_BASE_URL}/edit/${id}`,
          method: "PUT",
          body,
          headers: {
            "Content-Type": "application/json",
          },
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "ShipmentAction", id },
        { type: "ShipmentAction", id: "LIST" },
        { type: "ShipmentAction", id: "NOTIFICATION_LIST" },
      ],
    }),

    updateShipmentActionByShipmentId: builder.mutation({
      query: (payload) => {
        const { shipmentId, ...body } = payload;
        return {
          url: `${SHIPMENT_ACTION_BASE_URL}/edit/${shipmentId}`,
          method: "PUT",
          body,
          headers: {
            "Content-Type": "application/json",
          },
        };
      },
      invalidatesTags: (result, error, { shipmentId }) => [
        { type: "ShipmentAction", id: shipmentId },
        { type: "ShipmentAction", id: "LIST" },
        { type: "ShipmentAction", id: "NOTIFICATION_LIST" },
      ],
    }),
    getShipmentActionResponses: builder.query({
      query: (id) => `${SHIPMENT_ACTION_BASE_URL}/responses/${id}`,
      transformResponse: (response) => response,
      providesTags: (result, error, id) => [
        { type: "ShipmentAction", id },
        { type: "ShipmentAction", id: "LIST" },
      ],
    }),
    getDDLSystemVendorUserList: builder.query({
      query: (params) => ({
        url: `${SHIPMENT_ACTION_BASE_URL}/ddl/systemVendorUser`,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "User", id })),
              { type: "User", id: "DDL" },
            ]
          : [{ type: "User", id: "DDL" }],
    }),
    listShipmentListForBulkActions: builder.query({
      query: (params) => ({
        url: `${SHIPMENT_ACTION_BASE_URL}/ShipmentListForBulkActions`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "Shipment",
                id: _id,
              })),
              { type: "Shipment", id: "LIST" },
            ]
          : [{ type: "Shipment", id: "LIST" }],
    }),
    readAllShipmentActions: builder.mutation({
      query: () => ({
        url: `${SHIPMENT_ACTION_BASE_URL}/read-all`,
        method: "PUT",
      }),
      invalidatesTags: [{ type: "ShipmentAction", id: "LIST" }],
    }),
  }),
});

export const {
  useGetFilteredShipmentActionsQuery,
  useLazyGetFilteredShipmentActionsQuery,
  useGetShipmentActionListQuery,
  useLazyGetShipmentActionListQuery,
  useGetShipmentActionByShipmentIdQuery,
  useGetShipmentActionDetailQuery,
  useCreateShipmentActionMutation,
  useCreateShipmentActionBulkMutation,
  useUpdateShipmentActionMutation,
  useUpdateShipmentActionByShipmentIdMutation,
  useGetShipmentActionNotificationListQuery,
  useLazyGetShipmentActionNotificationListQuery,
  useGetShipmentActionResponsesQuery,
  useLazyGetShipmentActionResponsesQuery,

  useGetDDLSystemVendorUserListQuery,
  useLazyGetDDLSystemVendorUserListQuery,

  useLazyListShipmentListForBulkActionsQuery,
  useReadAllShipmentActionsMutation,
  useLazyGetShipmentActionListForUserQuery,
} = shipmentActionApi;
